import { useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { useStore } from '../../store/useStore';
import { Task, SubTask } from '../../types/task';
import { logger } from '../../utils/logger';

export const useTaskSync = () => {
  const store = useStore();

  useEffect(() => {
    if (!store.currentUser) return;

    // Initial fetch of all tasks with subtasks
    const fetchTasks = async () => {
      try {
        const { data: tasks, error } = await supabase
          .from('tasks')
          .select(`
            *,
            subtasks:subtasks(*),
            comments:task_comments(
              *,
              user:profiles(name, avatar)
            )
          `)
          .or(`assigned_to.eq.${store.currentUser.id},assigned_by.eq.${store.currentUser.id}`)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Map tasks to application format
        const mappedTasks = tasks.map((task): Task => ({
          id: task.id,
          title: task.title,
          description: task.description || '',
          assignedTo: task.assigned_to,
          assignedBy: task.assigned_by,
          department: task.department,
          priority: task.priority,
          status: task.status,
          dueDate: new Date(task.due_date),
          createdAt: new Date(task.created_at),
          updatedAt: new Date(task.updated_at),
          acknowledged: task.acknowledged,
          acknowledgedAt: task.acknowledged_at ? new Date(task.acknowledged_at) : undefined,
          comments: task.comments?.map(comment => ({
            id: comment.id,
            taskId: comment.task_id,
            userId: comment.user_id,
            content: comment.content,
            createdAt: new Date(comment.created_at),
            userName: comment.user?.name || 'Unknown',
            userAvatar: comment.user?.avatar || ''
          })) || [],
          subtasks: task.subtasks?.map((subtask): SubTask => ({
            id: subtask.id,
            title: subtask.title,
            description: subtask.description || '',
            dueDate: subtask.due_date ? new Date(subtask.due_date) : undefined,
            status: subtask.status,
            acknowledged: subtask.acknowledged,
            acknowledgedAt: subtask.acknowledged_at ? new Date(subtask.acknowledged_at) : undefined,
            completedAt: subtask.completed_at ? new Date(subtask.completed_at) : undefined,
            hasDueDate: !!subtask.due_date
          })) || []
        }));

        // Update store with fetched tasks
        store.setTasks(mappedTasks);
      } catch (error) {
        logger.error('Error fetching tasks:', error);
      }
    };

    fetchTasks();

    // Subscribe to task changes
    const taskSubscription = supabase
      .channel('task_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: `assigned_to=eq.${store.currentUser.id}`
        },
        async (payload) => {
          logger.info('Task change received:', payload);
          
          try {
            const { data: task, error } = await supabase
              .from('tasks')
              .select(`
                *,
                subtasks:subtasks(*),
                comments:task_comments(
                  *,
                  user:profiles(name, avatar)
                )
              `)
              .eq('id', payload.new.id)
              .single();

            if (error) throw error;

            // Map task to application format
            const mappedTask: Task = {
              id: task.id,
              title: task.title,
              description: task.description || '',
              assignedTo: task.assigned_to,
              assignedBy: task.assigned_by,
              department: task.department,
              priority: task.priority,
              status: task.status,
              dueDate: new Date(task.due_date),
              createdAt: new Date(task.created_at),
              updatedAt: new Date(task.updated_at),
              acknowledged: task.acknowledged,
              acknowledgedAt: task.acknowledged_at ? new Date(task.acknowledged_at) : undefined,
              comments: task.comments?.map(comment => ({
                id: comment.id,
                taskId: comment.task_id,
                userId: comment.user_id,
                content: comment.content,
                createdAt: new Date(comment.created_at),
                userName: comment.user?.name || 'Unknown',
                userAvatar: comment.user?.avatar || ''
              })) || [],
              subtasks: task.subtasks?.map(subtask => ({
                id: subtask.id,
                title: subtask.title,
                description: subtask.description || '',
                dueDate: subtask.due_date ? new Date(subtask.due_date) : undefined,
                status: subtask.status,
                acknowledged: subtask.acknowledged,
                acknowledgedAt: subtask.acknowledged_at ? new Date(subtask.acknowledged_at) : undefined,
                completedAt: subtask.completed_at ? new Date(subtask.completed_at) : undefined,
                hasDueDate: !!subtask.due_date
              })) || []
            };

            // Update store based on event type
            switch (payload.eventType) {
              case 'INSERT':
                store.addTask(mappedTask);
                break;
              case 'UPDATE':
                store.updateTask(mappedTask.id, mappedTask);
                break;
              case 'DELETE':
                store.deleteTask(payload.old.id);
                break;
            }
          } catch (error) {
            logger.error('Error processing task change:', error);
          }
        }
      )
      .subscribe();

    // Subscribe to subtask changes
    const subtaskSubscription = supabase
      .channel('subtask_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subtasks'
        },
        fetchTasks
      )
      .subscribe();

    // Subscribe to comment changes
    const commentSubscription = supabase
      .channel('comment_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_comments'
        },
        fetchTasks
      )
      .subscribe();

    return () => {
      taskSubscription.unsubscribe();
      subtaskSubscription.unsubscribe();
      commentSubscription.unsubscribe();
    };
  }, [store.currentUser]);
};